import { useCallback } from "react";
import {
  BooleanInput,
  Create,
  DateInput,
  ReferenceInput,
  SelectInput,
  SimpleForm,
  TextInput,
  useNotify,
  useRefresh,
} from "react-admin";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import NoArrowKeyNumberInput from "@/components/NoArrowKeyNumberInput";
import { slotNameMap } from "@/constants";
import { formatListDate } from "@/utils/date-format";
import { hallSlotMatchesRazaEventDate, toUtcYmd } from "@/utils/hall-slot-matches-raza-event-date";
import { purposeHelperText } from "@/utils/purpose-helper-text";

export type RazaRequestCreateHallSlotDialogProps = {
  open: boolean;
  onClose: () => void;
  bookingId: string;
  razaEventId: string;
  eventDate: string | Date;
  /** Prefill purpose from the raza event when available. */
  eventPurpose?: string | null;
};

const RazaRequestCreateHallSlotDialog = ({
  open,
  onClose,
  bookingId,
  razaEventId,
  eventDate,
  eventPurpose,
}: RazaRequestCreateHallSlotDialogProps) => {
  const refresh = useRefresh();
  const notify = useNotify();

  const eventDateIso = toUtcYmd(eventDate) ?? "";
  const eventDateLabel = formatListDate(eventDateIso || eventDate, { empty: "—", utc: true });

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  if (!open) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Create hall slot for this event
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "text.secondary",
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Creates a new hall slot on this booking, linked to the event. Date must stay on{" "}
          {eventDateLabel}.
        </Typography>
        <Create
          resource="hallBookings"
          redirect={false}
          mutationOptions={{
            onSuccess: () => {
              notify("Hall slot created and linked", { type: "success" });
              handleClose();
              refresh();
            },
            onError: (error: unknown) => {
              notify(error instanceof Error ? error.message : "Failed to create hall slot", {
                type: "error",
              });
            },
          }}
        >
          <SimpleForm
            defaultValues={{
              bookingId,
              razaEventId,
              date: eventDateIso || undefined,
              purpose: eventPurpose?.trim() ? eventPurpose.trim() : undefined,
              thaals: 0,
              withAC: false,
            }}
            validate={(values) => {
              const errors: Record<string, string> = {};
              if (!values.hallId) errors.hallId = "Required";
              if (!values.purpose) errors.purpose = "Required";
              if (!values.slot) errors.slot = "Required";
              if (!values.date) {
                errors.date = "Required";
              } else if (
                !hallSlotMatchesRazaEventDate(values.date as string | Date | undefined, eventDate)
              ) {
                errors.date = `Date must match the event (${eventDateLabel})`;
              }
              return errors;
            }}
          >
            <TextInput source="bookingId" sx={{ display: "none" }} />
            <TextInput source="razaEventId" sx={{ display: "none" }} />
            <ReferenceInput source="hallId" reference="halls">
              <SelectInput optionText="name" fullWidth />
            </ReferenceInput>
            <ReferenceInput source="purpose" reference="bookingPurpose">
              <SelectInput optionText="name" fullWidth helperText={purposeHelperText("hallSlot")} />
            </ReferenceInput>
            <NoArrowKeyNumberInput source="thaals" label="Thaals" fullWidth />
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, width: "100%" }}>
              <DateInput source="date" label="Date" fullWidth />
              <Button
                variant="text"
                size="small"
                endIcon={<OpenInNewIcon fontSize="small" />}
                sx={{ minWidth: 200, mt: 1 }}
                onClick={() => {
                  window.open("#/hallBookings", "_blank", "noopener,noreferrer");
                }}
              >
                Check hall availability
              </Button>
            </Box>
            <SelectInput
              source="slot"
              label="Slot"
              choices={Object.entries(slotNameMap).map(([slotId, name]) => ({
                id: slotId,
                name,
              }))}
              fullWidth
            />
            <BooleanInput source="withAC" label="With AC" fullWidth />
            <TextInput source="remarks" label="Remarks" fullWidth multiline minRows={2} />
          </SimpleForm>
        </Create>
      </DialogContent>
    </Dialog>
  );
};

export default RazaRequestCreateHallSlotDialog;
